import React, { useState } from 'react';

import { Navigate } from 'react-router-dom';

import Button from '@/components/ui/buttons/Button';
import FormattedOrganizationMembershipCode from '@/components/ui/formats/FormattedOrganizationMembershipCode';
import GradeClassNumberLabel from '@/components/ui/formats/GradeClassNumberLabel';
import Checkbox from '@/components/ui/forms/Checkbox';
import ErrorList from '@/components/ui/forms/ErrorList';
import DescriptionList from '@/components/ui/lists/DescriptionList';
import DescriptionDetails from '@/components/ui/lists/descriptionList/DescriptionDetails';
import DescriptionListItem from '@/components/ui/lists/descriptionList/DescriptionListItem';
import DescriptionTerm from '@/components/ui/lists/descriptionList/DescriptionTerm';
import useRequestRegistrationTokens from '@/hooks/registration/useRequestRegistrationTokens';
import {
  type AccountRegistrationFormValues,
  type StudentInfo,
} from '@/interfaces/registration';

import CSSModule from './AccountRegistrationConfirmation.module.scss';

interface Props {
  formValues: AccountRegistrationFormValues;
  studentInfo: StudentInfo;
  isPC: boolean;
  resendErrorStatus?: number;
  onClickAccountRegistration: (data?: {
    parent_email_registration_token_id: string;
  }) => void;
}
const AccountRegistrationConfirmation: React.FC<Props> = ({
  formValues,
  studentInfo,
  isPC,
  resendErrorStatus,
  onClickAccountRegistration,
}) => {
  const [isOrganizationMember, setIsOrganizationMember] = useState(false);

  const { sendRegistrationToken, isPending, isError, error } =
    useRequestRegistrationTokens();

  const is500Error =
    (isError && error?.status === 500) || resendErrorStatus === 500;
  const hasAnyError = isError || resendErrorStatus !== undefined;
  const hasNon500Error = hasAnyError && !is500Error;

  // エラーメッセージの決定
  const errors = !hasAnyError
    ? []
    : is500Error
      ? ['予期しないエラーが発生しました。時間をおいて再度お試しください']
      : [
          '予期しないエラーが発生し、登録できませんでした\r\n入力内容の確認をしてください',
        ];

  const buttonDisabled = !isOrganizationMember || isPending || hasNon500Error;

  const handleSubmit = () => {
    // リクエストボディを作成
    const { password_confirmation: _, ...requestBody } = {
      ...formValues,
    };

    sendRegistrationToken(requestBody, onClickAccountRegistration);
  };

  const studentData = studentInfo;

  // 生徒氏名（かな）を含む全フォーム値に欠損がないかを確認する
  const isAnyFormValuesNotFilled = Object.values(formValues).some(
    (value) => value === '' || value === null,
  );

  // studentDataが存在しない場合またはフォームに欠損がある場合はリダイレクト
  if (isAnyFormValuesNotFilled || !studentData) {
    return <Navigate replace to="/registration" />;
  }

  const organizationName = studentData.organization_name;
  const studentName = studentData.name;
  const gradeLabel = studentData.grade;
  const classNameLabel = studentData.class_name;
  const attendanceNumberLabel = studentData.attendance_number;

  return (
    <>
      <div
        className={
          isPC
            ? CSSModule.AccountRegistrationConfirmation
            : CSSModule.AccountRegistrationConfirmation__Mobile
        }
      >
        <DescriptionList>
          <DescriptionListItem
            className={
              isPC
                ? CSSModule.DescriptionListItem
                : CSSModule.DescriptionListItem__Mobile
            }
          >
            <DescriptionTerm>団体会員コード</DescriptionTerm>
            <DescriptionDetails>
              <FormattedOrganizationMembershipCode code={formValues.code} />
            </DescriptionDetails>
          </DescriptionListItem>
          <DescriptionListItem
            className={
              isPC
                ? CSSModule.DescriptionListItem
                : CSSModule.DescriptionListItem__Mobile
            }
          >
            <DescriptionTerm>保護者氏名</DescriptionTerm>
            <DescriptionDetails>
              {formValues.last_name}
              &nbsp; &nbsp;
              {formValues.first_name}
            </DescriptionDetails>
          </DescriptionListItem>
          <DescriptionListItem
            className={
              isPC
                ? CSSModule.DescriptionListItem
                : CSSModule.DescriptionListItem__Mobile
            }
          >
            <DescriptionTerm>保護者氏名（かな）</DescriptionTerm>
            <DescriptionDetails>
              {formValues.last_name_kana}
              &nbsp; &nbsp;
              {formValues.first_name_kana}
            </DescriptionDetails>
          </DescriptionListItem>
          <DescriptionListItem
            className={
              isPC
                ? CSSModule.DescriptionListItem
                : CSSModule.DescriptionListItem__Mobile
            }
          >
            <DescriptionTerm>メールアドレス</DescriptionTerm>
            <DescriptionDetails>{formValues.email}</DescriptionDetails>
          </DescriptionListItem>
          <DescriptionListItem
            className={
              isPC
                ? CSSModule.DescriptionListItem
                : CSSModule.DescriptionListItem__Mobile
            }
          >
            <DescriptionTerm>パスワード</DescriptionTerm>
            <DescriptionDetails>●●●●●●●●●</DescriptionDetails>
          </DescriptionListItem>
        </DescriptionList>
      </div>

      <div className={CSSModule.OrganizationMemberInformation}>
        <div
          className={
            CSSModule.OrganizationMemberInformation__StudentInformation
          }
        >
          <div
            className={
              CSSModule.OrganizationMemberInformation__StudentInformation__OrganizationName
            }
          >
            {organizationName}
          </div>
          <GradeClassNumberLabel
            className={
              CSSModule.OrganizationMemberInformation__StudentInformation__GradeClassNumberLabel
            }
            gradeLabel={gradeLabel}
            classNameLabel={classNameLabel}
            attendanceNumberLabel={attendanceNumberLabel}
          />
          <div
            className={
              CSSModule.OrganizationMemberInformation__StudentInformation__StudentName
            }
          >
            {studentName}
          </div>
        </div>

        <div className={CSSModule.OrganizationMemberInformation__CheckBoxArea}>
          <Checkbox
            checked={isOrganizationMember}
            onChange={() => setIsOrganizationMember(!isOrganizationMember)}
            disabled={isPending || hasNon500Error}
            role="checkbox"
          >
            <div
              className={
                CSSModule.OrganizationMemberInformation__Checkbox__Text
              }
            >
              私はこの生徒の保護者であることを確認しました
            </div>
          </Checkbox>
        </div>
      </div>
      <div className={CSSModule.SubmitButtonArea}>
        <ul className={CSSModule.SubmitButtonArea__Note}>
          <li>
            生徒情報（年・組・番・氏名）が誤っている場合は、学校にお問い合わせするか、戻るボタンより団体会員コードを再設定してください。
          </li>
        </ul>
        {errors.length > 0 && (
          <ErrorList
            className={CSSModule.SubmitButtonArea__Error}
            errors={errors}
          />
        )}
        <Button
          className={CSSModule.SubmitButton}
          color="primary"
          type="submit"
          disabled={buttonDisabled}
          onClick={handleSubmit}
        >
          仮登録をする
        </Button>
      </div>
    </>
  );
};

export default AccountRegistrationConfirmation;
